import { Creative, LineItemCreative } from '@/common/models';
import CreativeStatus from '@/common/models/creative/CreativeStatus.enum';
import Status from '@/common/models/lineitem/LineItemStatus.enum';
import QueryParams from '@/common/models/query-params/QueryParams';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import rootStore from '@/store';
import * as modelTypes from '@/store/modules/models/mutation-types';
import {
  createApiAction,
  createAuditAction,
  createDeleteAction,
  createGetAllAction,
  createGetOneAction,
  createPatchAction,
  createPostAction,
  createPutAction,
} from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  createNewCreative({ commit }, creative = new Creative(-1, '')) {
    commit(types.CREATE_CREATIVE, creative);
    return creative;
  },

  getExternalCreatives: createGetAllAction(
    API.instance.getCreatives,
    types.SAVE_EXTERNAL_CREATIVES,
    'messages.failedToGetCreatives',
  ),
  getCreatives: createGetAllAction(
    API.instance.getCreatives,
    types.SAVE_CREATIVES,
    'messages.failedToGetCreatives',
    false,
  ),
  getCreative: createGetOneAction(
    API.instance.getCreative,
    types.UPDATE_CREATIVE,
    'messages.failedToGetCreative',
    true,
  ),
  createCreatives: createPostAction(
    API.instance.postCreatives,
    types.SAVE_CREATIVES,
    'messages.failedToCreateCreative',
  ),
  updateCreative: createPutAction(API.instance.putCreative, types.UPDATE_CREATIVE, 'messages.failedToUpdateCreative'),
  deleteCreative: createDeleteAction(
    API.instance.deleteCreative,
    types.DELETE_CREATIVE,
    'messages.failedToDeleteCreative',
  ),
  getCreativeAudit: createAuditAction(API.instance.getCreativeAudit, 'messages.failedToGetCreativeAudit', false),
  getCreativeMediaOwnerApprovalTargetedNetworks: createApiAction(
    API.instance.getCreativeMediaOwnerApprovalTargetedNetworks,
  ),
  getCreativeMediaOwnerApprovalInventoryTargeting: createGetOneAction(
    API.instance.getCreativeMediaOwnerApprovalInventoryTargeting,
    null,
    'messages.failedToGetCreatives',
    false,
  ),
  convertCreativeMediaOwnerApproval: createApiAction(API.instance.convertCreativeMediaOwnerApproval),
  updateCreativeMediaOwnerApproval: createApiAction(
    API.instance.putCreativeMediaOwnerApprovals,
    null,
    'messages.failedToUpdateCreativeMediaOwnerApprovals',
  ),
  patchCreatives: createPatchAction(API.instance.patchCreatives, {
    overall: 'messages.creativesBatchOperationFailed',
    adding: 'messages.failedToCreateCreatives',
    updating: 'messages.failedToUpdateCreatives',
    deleting: 'messages.failedToDeleteCreatives',
  }),

  getCreativeFiles({ commit }, queryParams) {
    if (queryParams.showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getCreativeFiles(
        queryParams,
        (result) => {
          commit(types.SAVE_CREATIVES, result.items);
          resolve(result);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCreativesFiles');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  getCreativeCreativeFiles({ commit }, queryParams) {
    return new Promise((resolve, reject) => {
      if (queryParams.showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      API.instance.getCreativeFiles(
        queryParams,
        (result) => {
          commit(types.UPDATE_CREATIVEFILES_LIST, result.items);
          resolve(result);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCreativesFiles');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  linkLineItemsToCreative({ commit }, change) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);
      const lineItemCreatives = change.lineItems.map((x) => new LineItemCreative(-1, change.creative.id, x.id));
      API.instance.postLineItemCreatives(
        lineItemCreatives,
        (result) => {
          commit(modelTypes.UPDATE_CREATIVE_LINEITEMS, { creative: change.creative, value: result }, { root: true });
          GlobalEventBus.$emit(GlobalEvents.loaded);
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToLinkLineItemsToCreative');
          ErrorHandler.showError(msg, error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
          reject(error);
        },
      );
    });
  },

  saveCreativeState({ commit }, creative) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.putCreativeState(
        creative,
        (data) => {
          commit(types.SAVE_CREATIVE, data);
          resolve(data);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveCreativeState');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  async saveCreativeWithoutApproval(_, creative) {
    // BE expects the creative to be in DRAFT first > SUBMITTED > APPROVED to be saved correctly
    rootStore.dispatch('updateCreativeState', { creative, value: CreativeStatus.SUBMITTED });
    await rootStore.dispatch('orgApp/saveCreativeState', creative);
    rootStore.dispatch('updateCreativeState', { creative, value: CreativeStatus.APPROVED });
    await rootStore.dispatch('orgApp/saveCreativeApproval', { creative });
  },

  async saveCreativeApproval({ commit }, { creative, addEmptyInventoryTargeting = false, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const updatedCreative = await API.instance
      .putCreativeApproval(creative, addEmptyInventoryTargeting)
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToSaveCreativeState');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    commit(types.SAVE_CREATIVE, updatedCreative);

    return updatedCreative;
  },

  updateCreativePrefetchType({ commit }, change) {
    commit(types.UPDATE_CREATIVE_PREFETCH_TYPE, change);
  },

  updateCreativePrefetchSource({ commit }, change) {
    commit(types.UPDATE_CREATIVE_PREFETCH_SOURCE, change);
  },

  updateCreativePrefetchID({ commit }, change) {
    commit(types.UPDATE_CREATIVE_PREFETCH_ID, change);
  },

  updateCreativePrefetchAllSizesApproval({ commit }, change) {
    commit(types.UPDATE_CREATIVE_PREFETCH_ALL_SIZES_APPROVAL, change);
  },

  updateCreativePrefetchCacheType({ commit }, change) {
    commit(types.UPDATE_CREATIVE_PREFETCH_CACHE_TYPE, change);
  },

  updateCreativeIsFallback({ commit }, change) {
    commit(types.UPDATE_CREATIVE_IS_FALLBACK, change);
  },

  saveCreativeFile({ commit }, { creativeFile, creative, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    return new Promise((resolve, reject) => {
      API.instance.postCreativeFile(
        creativeFile,
        (data) => {
          if (creative.state !== Status.DRAFT) {
            commit(modelTypes.UPDATE_CREATIVE_STATE, { creative, value: Status.DRAFT }, { root: true });
          }
          commit(types.SAVE_CREATIVE, data);
          commit(types.ADD_CREATIVEFILE_TO_CREATIVE, {
            creative,
            creativeFile,
          });
          resolve();

          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveCreative');
          ErrorHandler.showError(msg, error);
          reject(error);

          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  /**
   * Letterbox the creative file
   * @params payload {url: string, name: string, dimensions: [number. number][]}[]
   */
  resizeCreativeFile({ commit }, { payload, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.resizeCreativeFile(
        payload,
        (data) => {
          resolve(data);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveCreative');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  uploadCreativeFile({ commit }, { file, creativeId, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.uploadCreativeFile(
        file,
        creativeId,
        (data) => {
          resolve(data);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveCreative');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  deleteCreativeFiles({ commit }, creativeAndCreativeFiles) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = (error) => {
        if (successfullApiCalls + failedApiCalls === creativeAndCreativeFiles.creativeFiles.length) {
          if (successfullApiCalls === creativeAndCreativeFiles.creativeFiles.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject(error);
            const msg = i18n.global.t('messages.failedToDeleteCreativeFiles');
            ErrorHandler.showError(msg, error);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      (creativeAndCreativeFiles.creativeFiles || []).forEach((creativeFile) => {
        API.instance.deleteCreativeFile(
          creativeFile.id,
          () => {
            commit(types.DELETE_CREATIVEFILE, { creative: creativeAndCreativeFiles.creative, creativeFile });
            successfullApiCalls += 1;
            evalCompletion();
          },
          (error) => {
            failedApiCalls += 1;
            evalCompletion(error);
          },
        );
      });
    });
  },

  deleteCreativeFile({ commit }, creativeFile) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deleteCreativeFile(
        creativeFile.id,
        () => {
          commit(types.DELETE_CREATIVEFILE, creativeFile);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeleteCreativeFile');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  updateCreativeFilePrefetchApprovalState({ commit }, { creativeFileIDs, newState }) {
    if (creativeFileIDs.length <= 0) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.updateCreativeFilePrefetchApprovalState(
        creativeFileIDs,
        newState,
        (result) => {
          commit(types.UPDATE_CREATIVEFILE_APPROVAL_STATES, result);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveCreativeFile');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  approveCreativePrefetchSizes({ commit }, { creative, sizes }) {
    if (sizes.length <= 0) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.approveCreativePrefetchSizes(
        creative,
        sizes,
        () => {
          const sizeApprovals = sizes.map((x) => ({
            width: x.width,
            height: x.height,
            approval: CreativeStatus.APPROVED,
          }));
          commit(types.UPDATE_CREATIVE_PREFETCH_SIZE_APPROVAL_STATES, { creative, sizeApprovals });
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToUpdateCreativePrefetchSizeApproval');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  rejectCreativePrefetchSizes({ commit }, { creative, sizes }) {
    if (sizes.length <= 0) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.rejectCreativePrefetchSizes(
        creative,
        sizes,
        () => {
          const sizeApprovals = sizes.map((x) => ({
            width: x.width,
            height: x.height,
            approval: CreativeStatus.REJECTED,
          }));
          commit(types.UPDATE_CREATIVE_PREFETCH_SIZE_APPROVAL_STATES, { creative, sizeApprovals });
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToUpdateCreativePrefetchSizeApproval');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  deleteCreativeLineItems({ commit }, creativeAndCreativeLineItems) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = (error) => {
        if (successfullApiCalls + failedApiCalls === creativeAndCreativeLineItems.lineItems.length) {
          if (successfullApiCalls === creativeAndCreativeLineItems.lineItems.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject(error);
            const msg = i18n.global.t('messages.failedToDeleteCreativeLineItems');
            ErrorHandler.showError(msg, error);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      (creativeAndCreativeLineItems.lineItems || []).forEach((lineItem) => {
        API.instance.deleteCreativeLineItems(
          {
            creativeID: creativeAndCreativeLineItems.creative.id,
            lineItemID: lineItem.id,
          },
          () => {
            commit(types.DELETE_CREATIVE_LINEITEM, {
              creative: creativeAndCreativeLineItems.creative,
              lineItemID: lineItem.id,
            });
            successfullApiCalls += 1;
            evalCompletion();
          },
          (error) => {
            failedApiCalls += 1;
            evalCompletion(error);
          },
        );
      });
    });
  },

  deleteCreativeLineItem({ commit }, creativeAndCreativeLineItems) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deleteCreativeLineItems(
        {
          creativeID: creativeAndCreativeLineItems.creative.id,
          lineItemID: creativeAndCreativeLineItems.lineItem.id,
        },
        () => {
          commit(types.DELETE_CREATIVE_LINEITEM, {
            creative: creativeAndCreativeLineItems.creative,
            lineItemID: creativeAndCreativeLineItems.lineItem.id,
          });
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeleteLineItemCreative');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getCreativeLineItemMissingDimensions({ commit }, creativeIDs) {
    return new Promise((resolve, reject) => {
      // GlobalEventBus.$emit(GlobalEvents.loading);
      API.instance.getCreativeLineItemMissingDimensions(
        creativeIDs,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCreativeLineItems');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getCreativeMediaOwnerApprovals(
    { commit },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = false } = {},
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const approvals = await API.instance
      .getCreativeMediaOwnerApprovals({ queryParams, cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetCreativeMediaOwnerApprovals');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return approvals;
  },
  //
  // async getCreativeMediaOwnerApprovalTargetedNetworks({ commit }, { id, cancelTokenSource = null, showLoader = false } = {}) { // eslint-disable-line
  //   if (showLoader) {
  //     GlobalEventBus.$emit(GlobalEvents.loading);
  //   }
  //   const targetedNetworks = await API.instance.getCreativeMediaOwnerApprovalTargetedNetworks({ params: { id }, cancelTokenSource })
  //     .catch((err) => {
  //       const msg = i18n.global.t('messages.failedToGetCreativeMediaOwnerApprovalTargetedNetworks');
  //       ErrorHandler.showError(msg, err);
  //       if (showLoader) {
  //         GlobalEventBus.$emit(GlobalEvents.loaded);
  //       }
  //       throw err;
  //     });
  //   if (showLoader) {
  //     GlobalEventBus.$emit(GlobalEvents.loaded);
  //   }
  //
  //   return targetedNetworks;
  // }
};

export default actions;
